/**
 * The content package.
 *
 * Curriculum content lives here and *only* here — no lesson text, no exercise
 * parameters and no misconception wording appear in application code (§23). The
 * app imports the registry and the bank; it never imports a lesson directly.
 */

import { CurriculumRegistry, type Curriculum } from '@yuan-math/curriculum';
import { ExerciseBank, MISCONCEPTIONS } from '@yuan-math/exercises';
import type { GhostWorld } from '@yuan-math/world';
import { CONCEPTS } from './concepts/index.ts';
import { LESSONS } from './lessons/index.ts';
import { EXERCISE_TEMPLATES } from './exercises/index.ts';
import { COURSES, UNITS } from './courses.ts';
import { LEVELS, STRANDS } from './levels.ts';
import { KINGDOMS } from './world/kingdoms.ts';
import { QUESTS } from './world/quests.ts';
import { BOSSES } from './world/bosses.ts';
import { ITEMS, TITLES } from './world/items.ts';
import { OVERWORLD } from './world/overworld.ts';
import { ISLES } from './world/isles.ts';
import { TOWNS } from './world/towns.ts';
import { REGION_MAPS } from './world/region-maps.ts';
import { GHOST_ARCHETYPES } from './world/ghosts.ts';

export const curriculum: Curriculum = {
  levels: LEVELS,
  strands: STRANDS,
  concepts: CONCEPTS,
  courses: COURSES,
  units: UNITS,
  lessons: LESSONS,
};

/** The single registry instance the application queries. */
export const registry = new CurriculumRegistry(curriculum);

/** The single exercise bank instance. */
export const bank = new ExerciseBank(EXERCISE_TEMPLATES);

export { CONCEPTS, LESSONS, COURSES, UNITS, LEVELS, STRANDS, EXERCISE_TEMPLATES };
/** The RPG world map, grouping the concepts above into kingdoms and regions. */
export { KINGDOMS };
/** The quest lines laid over that map. Story only — the mathematics is above. */
export { QUESTS };
/** The bosses guarding those kingdoms. Their questions come from the bank above. */
export { BOSSES };
/** Cosmetics and honorifics. Strictly a drawing and a name — nothing reads them. */
export { ITEMS, TITLES };
/** The walkable tile map those kingdoms stand on (docs/rpg.md §22). */
export { OVERWORLD };
/**
 * The offshore isles, opened by achievements. Drawings and views only — an
 * `Isle` has nowhere to put a concept, which is what makes a shut gate allowable.
 */
export { ISLES };
/** And the inside of a kingdom, at village scale, for the ones surveyed so far. */
export { TOWNS };
/** And one scale further in: the inside of a region, drawn as the place it is named after. */
export { REGION_MAPS };
/** Costume for the bestiary: one archetype per kingdom. The creatures are derived. */
export { GHOST_ARCHETYPES };

/**
 * The bestiary, bound to this registry, this world and the real catalog.
 *
 * A value rather than a list, because a ghost is *derived* from those three
 * things (`packages/world/src/ghosts.ts`) — there is no authored ghost anywhere
 * to fall out of step with the mathematics, and nothing to migrate when a
 * misconception's remediation concept changes.
 */
export const ghosts: GhostWorld = {
  archetypes: GHOST_ARCHETYPES,
  kingdoms: KINGDOMS,
  registry,
  misconceptions: MISCONCEPTIONS,
};
export * from './concepts/index.ts';
